import React from 'react';
import './SessionDetailsOverlay.css';
import { shortenStellarAddress } from './utils/stellarDisplay';

interface Session {
  sessionId: number;
  player1: string | null;
  player2: string | null;
  state: string;
  p1CellId?: number;
  p2CellId?: number;
  p1Country?: number;
  p2Country?: number;
  createdLedger?: number;
  players?: Array<{ address: string; cell_id: number; asset_tag: any; country: number; location_proof?: any }>;
  isPublic?: boolean;
  is_public?: boolean;
}

interface SessionDetailsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  currentSession: Session | null;
  allSessions: Session[];
  walletAddress: string | null;
  onJoinSession: (sessionId: number) => Promise<void>;
  onEndSession?: (sessionId: number) => Promise<void>;
}

export const SessionDetailsOverlay: React.FC<SessionDetailsOverlayProps> = ({
  isOpen,
  onClose,
  currentSession,
  allSessions,
  walletAddress,
  onJoinSession,
  onEndSession,
}) => {
  if (!isOpen) return null;

  const otherSessions = allSessions.filter(s => s.sessionId !== currentSession?.sessionId);

  return (
    <div className="session-details-overlay" onClick={onClose}>
      <div className="session-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="session-details-header">
          <h2>Session Details</h2>
          <button className="session-details-close" onClick={onClose}>×</button>
        </div>
        
        <div className="session-details-content">
          {/* Current Session Details */}
          {currentSession && (
            <div className="session-details-section">
              <h3>Your Current Session</h3>
              <div className="session-info-card">
                <div className="session-info-row">
                  <span className="session-info-label">Session ID:</span>
                  <span className="session-info-value">#{currentSession.sessionId}</span>
                </div>
                <div className="session-info-row">
                  <span className="session-info-label">Status:</span>
                  <span className={`session-status session-status-${currentSession.state.toLowerCase()}`}>
                    {currentSession.state}
                    {(currentSession.isPublic || currentSession.is_public) && <span style={{ marginLeft: '8px', color: '#28a745', fontSize: '12px' }}>(Public)</span>}
                  </span>
                </div>
                {/* Public sessions: many participant addresses */}
                {currentSession.players && currentSession.players.length > 0 ? (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallets in session ({currentSession.players.length}):</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                      {currentSession.players.map((p: any, idx: number) => (
                        <div key={idx} style={{ fontSize: '12px' }}>
                          {shortenStellarAddress(p.address)}
                          {p.address === walletAddress && <span className="session-you-badge"> (You)</span>}
                          {p.cell_id && <span style={{ color: '#666', marginLeft: '8px' }}>(Cell: {p.cell_id})</span>}
                          {p.country && <span style={{ color: '#666', marginLeft: '8px' }}>(Country: {p.country})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                {currentSession.player1 && (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallet 1:</span>
                    <span className="session-info-value">
                      {shortenStellarAddress(currentSession.player1)}
                      {currentSession.player1 === walletAddress && <span className="session-you-badge"> (You)</span>}
                    </span>
                  </div>
                )}
                {currentSession.p1CellId && (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallet 1 cell:</span>
                    <span className="session-info-value">{currentSession.p1CellId}</span>
                  </div>
                )}
                {currentSession.p1Country && (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallet 1 country:</span>
                    <span className="session-info-value">{currentSession.p1Country}</span>
                  </div>
                )}
                {currentSession.player2 && (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallet 2:</span>
                    <span className="session-info-value">
                      {shortenStellarAddress(currentSession.player2)}
                      {currentSession.player2 === walletAddress && <span className="session-you-badge"> (You)</span>}
                    </span>
                  </div>
                )}
                {currentSession.p2CellId && (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallet 2 cell:</span>
                    <span className="session-info-value">{currentSession.p2CellId}</span>
                  </div>
                )}
                {currentSession.p2Country && (
                  <div className="session-info-row">
                    <span className="session-info-label">Wallet 2 country:</span>
                    <span className="session-info-value">{currentSession.p2Country}</span>
                  </div>
                )}
                  </>
                )}
                {currentSession.createdLedger && (
                  <div className="session-info-row">
                    <span className="session-info-label">Created at Ledger:</span>
                    <span className="session-info-value">{currentSession.createdLedger}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Sessions */}
          <div className="session-details-section">
            <h3>Other Active Sessions ({otherSessions.length})</h3>
            {otherSessions.length === 0 ? (
              <div className="session-empty-state">
                <p>No other active sessions available.</p>
              </div>
            ) : (
              <div className="session-list">
                {otherSessions.map((session) => {
                  const canJoin = walletAddress && 
                    session.player1 !== walletAddress && 
                    session.player2 !== walletAddress &&
                    session.state === 'Waiting';
                  
                  return (
                    <div key={session.sessionId} className="session-item">
                      <div className="session-item-header">
                        <span className="session-item-id">Session #{session.sessionId}</span>
                        <span className={`session-item-status session-status-${session.state.toLowerCase()}`}>
                          {session.state}
                          {(session.isPublic || session.is_public) && <span style={{ marginLeft: '4px', color: '#28a745', fontSize: '10px' }}>(Public)</span>}
                        </span>
                      </div>
                      <div className="session-item-details">
                        {/* Public session participants */}
                        {session.players && session.players.length > 0 ? (
                          <div>
                            <strong>Wallets in session ({session.players.length}):</strong>
                            {session.players.map((p: any, idx: number) => (
                              <div key={idx} style={{ fontSize: '11px', marginTop: '2px' }}>
                                {shortenStellarAddress(p.address)}
                                {p.address === walletAddress && <span style={{ color: '#28a745', fontWeight: 'bold' }}> (You)</span>}
                                {p.cell_id && <span style={{ color: '#666' }}> (Cell: {p.cell_id})</span>}
                                {p.country && <span style={{ color: '#666' }}> (Country: {p.country})</span>}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <>
                        {session.player1 && (
                          <div className="session-item-wallet-row">
                            <strong>Wallet 1:</strong> {shortenStellarAddress(session.player1)}
                            {session.p1CellId && <span className="session-item-meta"> (Cell: {session.p1CellId})</span>}
                          </div>
                        )}
                        {session.player2 && (
                          <div className="session-item-wallet-row">
                            <strong>Wallet 2:</strong> {shortenStellarAddress(session.player2)}
                            {session.p2CellId && <span className="session-item-meta"> (Cell: {session.p2CellId})</span>}
                          </div>
                        )}
                        {!session.player2 && (
                          <div className="session-item-meta">Waiting for a second wallet…</div>
                        )}
                          </>
                        )}
                      </div>
                      {canJoin && (
                        <button
                          className="session-join-button"
                          onClick={async () => {
                            try {
                              await onJoinSession(session.sessionId);
                              onClose();
                            } catch (error: any) {
                            }
                          }}
                        >
                          Join Session
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
